import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Handshake } from 'lucide-react';
import { Button, Modal, Input, Loading, EmptyState, Badge } from '@/components/ui';
import OrgPicker, { PartnerNoOrgWarning } from '@/components/affiliate/OrgPicker';
import { formatNIS } from '@/utils/currency';
import { affiliateApi, type CreatePartnerInput, type PartnerListRow } from '@/api/affiliate';
import { orgUsersApi } from '@/api/orgUsers';
import { orgsApi } from '@/api/orgs';
import { listPartners as listWaPartners } from '@/api/partners';

type OwnerContact = { name: string; email: string; phone: string };

/** Owner contact for a JumpStart org. Name falls back to the org name when the owner has none. */
async function ownerContactForOrg(orgId: string): Promise<OwnerContact> {
  const [org, users] = await Promise.all([orgsApi.get(orgId), orgUsersApi.list(orgId)]);
  const owner =
    (org.owner_user_id ? users.find((u) => u.user_id === org.owner_user_id) : undefined) ??
    users.find((u) => u.role === 'owner');
  const ownerName = owner?.full_name?.trim() ?? '';
  return {
    name: ownerName || org.name,
    email: owner?.email?.trim() || org.email?.trim() || '',
    phone: owner?.phone?.trim() || org.phone?.trim() || '',
  };
}

function rewardLabel(row: PartnerListRow): string {
  if (!row.current_reward_type) return '—';
  return row.current_reward_type === 'cash_payout' ? 'Cash payout' : 'Discount';
}

export default function PartnersListPage() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: partners, isLoading } = useQuery({
    queryKey: ['affiliate-partners'],
    queryFn: () => affiliateApi.partners.list(),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Handshake className="h-5 w-5 text-indigo-400" />
          <div>
            <h1 className="text-xl font-semibold text-white">Affiliate Partners</h1>
            <p className="text-xs text-ink-muted mt-0.5">
              Partners, their live agreement, referred customers and payouts
            </p>
          </div>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus size={16} className="mr-1" /> New partner
        </Button>
      </div>

      {isLoading ? (
        <Loading text="Loading partners…" />
      ) : !(partners ?? []).length ? (
        <EmptyState
          icon={<Handshake className="h-8 w-8" />}
          title="No partners yet"
          description="Create a partner (e.g. Yan or Liana), then give them an agreement and a signup link."
          actionLabel="New partner"
          onAction={() => setModalOpen(true)}
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted text-xs uppercase">
                <th className="px-4 py-3">Partner</th>
                <th className="px-4 py-3">Reward</th>
                <th className="px-4 py-3">Payout</th>
                <th className="px-4 py-3">Active subs</th>
                <th className="px-4 py-3">MTD earnings</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {partners!.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/affiliate/partners/${p.id}`)}
                  className="border-t border-navy-border hover:bg-white/[0.02] cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{p.name}</div>
                    <div className="text-xs text-ink-muted">{p.contact_email ?? p.contact_phone ?? '—'}</div>
                  </td>
                  <td className="px-4 py-3">{rewardLabel(p)}</td>
                  <td className="px-4 py-3 capitalize">
                    {p.current_payout_mechanism ? p.current_payout_mechanism.replace(/_/g, ' ') : '—'}
                  </td>
                  <td className="px-4 py-3 num">{p.active_subs}</td>
                  <td className="px-4 py-3 num">{formatNIS(p.mtd_earnings_agorot)}</td>
                  <td className="px-4 py-3">
                    <Badge variant={p.is_active ? 'success' : 'neutral'}>
                      {p.is_active ? 'active' : 'inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreatePartnerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(id) => {
          setModalOpen(false);
          navigate(`/affiliate/partners/${id}`);
        }}
      />
    </div>
  );
}

function CreatePartnerModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [extPartnerId, setExtPartnerId] = useState('');
  const [linkedOrgId, setLinkedOrgId] = useState<string | null>(null);
  const typed = useRef({ name: false, email: false, phone: false });
  const fillSeq = useRef(0);

  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setPhone('');
      setExtPartnerId('');
      setLinkedOrgId(null);
      typed.current = { name: false, email: false, phone: false };
      fillSeq.current += 1;
    }
  }, [open]);

  const onOrgChange = (orgId: string | null) => {
    setLinkedOrgId(orgId);
    if (!orgId) return;
    const seq = ++fillSeq.current;
    void ownerContactForOrg(orgId)
      .then((contact) => {
        if (seq !== fillSeq.current) return;
        if (!typed.current.name && contact.name) setName(contact.name);
        if (!typed.current.email && contact.email) setEmail(contact.email);
        if (!typed.current.phone && contact.phone) setPhone(contact.phone);
      })
      .catch((e: Error) => {
        if (seq !== fillSeq.current) return;
        toast.error(e.message ?? 'Could not load the organization owner');
      });
  };

  // WhatsApp partners (mzalz) for the optional ext_partner_id soft-link.
  const { data: waPartners } = useQuery({
    queryKey: ['wa-partners-for-affiliate'],
    queryFn: () => listWaPartners(),
    enabled: open,
  });

  const createMut = useMutation({
    mutationFn: () => {
      const payload: CreatePartnerInput = {
        name,
        contact_email: email || null,
        contact_phone: phone || null,
        ext_partner_id: extPartnerId || null,
        linked_org_id: linkedOrgId,
      };
      return affiliateApi.partners.create(payload);
    },
    onSuccess: (partner) => {
      toast.success('Partner created');
      qc.invalidateQueries({ queryKey: ['affiliate-partners'] });
      onCreated(partner.id);
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <Modal isOpen={open} onClose={onClose} title="New affiliate partner">
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => {
            typed.current.name = true;
            setName(e.target.value);
          }}
          placeholder="Yan"
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Contact email"
            value={email}
            onChange={(e) => {
              typed.current.email = true;
              setEmail(e.target.value);
            }}
            type="email"
          />
          <Input
            label="Contact phone"
            value={phone}
            onChange={(e) => {
              typed.current.phone = true;
              setPhone(e.target.value);
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-secondary mb-1.5">
            Link to WhatsApp partner (optional)
          </label>
          <select
            value={extPartnerId}
            onChange={(e) => setExtPartnerId(e.target.value)}
            className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
          >
            <option value="">— none —</option>
            {(waPartners ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.display_name} ({w.slug})
              </option>
            ))}
          </select>
        </div>

        <OrgPicker
          label="JumpStart organization"
          value={linkedOrgId}
          onChange={onOrgChange}
          hint="The partner's own JumpStart organization. Leave empty only if they haven't registered yet."
        />

        <PartnerNoOrgWarning show={!linkedOrgId} />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!name.trim()) {
                toast.error('Name is required');
                return;
              }
              createMut.mutate();
            }}
            loading={createMut.isPending}
          >
            Create partner
          </Button>
        </div>
      </div>
    </Modal>
  );
}
